import React from 'react';
import {Image, Pressable, StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import {PlayerProfileAnonymous} from '@/Hooks/PlayerProfileApi/types';
import RatingCard from '../ui/RatingCard';

const initials = (name?: string | null) =>
  (name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

const styles = StyleSheet.create({
  // Design ("Player Profile - Anonymous & Unlinked.dc.html"): white hero card,
  // identity block on the left, a cream rating card pinned right.
  card: {
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingVertical: 26,
    paddingHorizontal: 28,
    marginBottom: 20,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 28,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  avatar: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: '#3DAB9E',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {width: 96, height: 96},
  avatarInitials: {color: '#fff', fontSize: 34, fontWeight: '600'},
  identityCol: {flex: 1, minWidth: 240},
  nameRow: {flexDirection: 'row', alignItems: 'center', gap: 9, flexWrap: 'wrap'},
  name: {fontSize: 24, color: '#262322'},
  // Brown chip w/ gold text, matching the logged-in ProfileHeader's FIDE chip.
  fideChip: {
    backgroundColor: '#4D3F37',
    borderRadius: 6,
    paddingHorizontal: 9,
    paddingVertical: 4,
  },
  fideChipText: {fontSize: 10.5, fontWeight: '700', color: '#FACF47'},
  metaLine: {fontSize: 13, color: '#888280', marginTop: 5},
  // Same cream stat pills as the logged-in ProfileHeader (label + bold value).
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 9,
    marginTop: 16,
  },
  statChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FAF6EB',
    borderRadius: 11,
    paddingVertical: 9,
    paddingHorizontal: 14,
  },
  statLabel: {fontSize: 13, lineHeight: 16, color: '#4D3F37'},
  statValue: {fontSize: 13, lineHeight: 16, fontWeight: '700', color: '#262322'},
  // Brown "Log in to challenge" CTA (design uses brown here, not the gold
  // primary, so it reads as the single dominant action).
  challengeBtn: {
    alignSelf: 'flex-start',
    marginTop: 18,
    backgroundColor: '#4D3F37',
    borderRadius: 32,
    paddingVertical: 13,
    paddingHorizontal: 24,
  },
  challengeBtnText: {fontSize: 13.5, fontWeight: '700', color: '#fff'},
});

type Props = {
  profile: PlayerProfileAnonymous;
  onLogin: () => void;
};

// Anonymous-context header (Milestone 4). Separate from ProfileHeader: the
// anonymous payload carries only identity + rating_history + teaser_stats, and
// the CTA is a single "Log in to challenge {name}" button (per the plan's
// design note), not the Challenge/Share pair.
export default function AnonymousHeader({profile, onLogin}: Props) {
  const name = profile.name || profile.username;
  const metaLine = [
    profile.username ? `@${profile.username}` : null,
    profile.location,
    profile.member_since ? `Member since ${profile.member_since}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box style={styles.card}>
      <View style={styles.avatar}>
        {profile.avatar_url ? (
          <Image source={{uri: profile.avatar_url}} style={styles.avatarImage} />
        ) : (
          <CCText style={styles.avatarInitials}>{initials(name)}</CCText>
        )}
      </View>

      <View style={styles.identityCol}>
        <View style={styles.nameRow}>
          <CCText fontWeight="semibold" style={styles.name}>
            {name}
          </CCText>
          {profile.fide_title ? (
            <View style={styles.fideChip}>
              <CCText style={styles.fideChipText}>{profile.fide_title}</CCText>
            </View>
          ) : null}
        </View>
        {metaLine ? <CCText style={styles.metaLine}>{metaLine}</CCText> : null}

        <View style={styles.statsRow}>
          <View style={styles.statChip}>
            <CCText style={styles.statLabel}>
              Games{' '}
              <CCText style={styles.statValue}>
                {profile.teaser_stats.games}
              </CCText>
            </CCText>
          </View>
          {profile.teaser_stats.win_rate != null ? (
            <View style={styles.statChip}>
              <CCText style={styles.statLabel}>
                Win rate{' '}
                <CCText style={styles.statValue}>
                  {Math.round(profile.teaser_stats.win_rate * 100)}%
                </CCText>
              </CCText>
            </View>
          ) : null}
          <View style={styles.statChip}>
            <CCText style={styles.statLabel}>
              Puzzle accuracy{' '}
              <CCText style={styles.statValue}>
                {profile.teaser_stats.puzzle_accuracy != null
                  ? `${Math.round(profile.teaser_stats.puzzle_accuracy * 100)}%`
                  : '—'}
              </CCText>
            </CCText>
          </View>
          {profile.teaser_stats.net_score != null ? (
            <View style={styles.statChip}>
              <CCText style={styles.statLabel}>
                Net score{' '}
                <CCText style={styles.statValue}>
                  {profile.teaser_stats.net_score}
                </CCText>
              </CCText>
            </View>
          ) : null}
        </View>

        <Pressable
          testID="anon-login-cta"
          style={styles.challengeBtn}
          onPress={onLogin}>
          <CCText style={styles.challengeBtnText}>
            Log in to challenge {name}
          </CCText>
        </Pressable>
      </View>

      <RatingCard
        rating={profile.gameplay_rating}
        history={profile.rating_history ?? []}
      />
    </Box>
  );
}
